import React, { useEffect, useRef } from 'react';

interface Node {
    x: number;
    y: number;
    vx: number;
    vy: number;
    r: number;
    hue: number;
}

const HeroAmbience: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        let width = 0;
        let height = 0;
        let raf = 0;
        let nodes: Node[] = [];
        let pointer = { x: 0, y: 0, active: false };

        const seed = () => {
            const count = Math.round(Math.min(64, Math.max(24, (width * height) / 24000)));
            nodes = Array.from({ length: count }, () => ({
                x: Math.random() * width,
                y: Math.random() * height,
                vx: (Math.random() - 0.5) * 0.22,
                vy: (Math.random() - 0.5) * 0.22,
                r: Math.random() * 1.6 + 0.8,
                hue: Math.random() > 0.65 ? 188 : 265,
            }));
        };

        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            width = Math.max(1, rect.width);
            height = Math.max(1, rect.height);
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            seed();
        };

        const draw = () => {
            ctx.clearRect(0, 0, width, height);
            const linkDist = Math.min(150, Math.max(96, width / 9));

            for (const n of nodes) {
                if (!reduceMotion) {
                    n.x += n.vx;
                    n.y += n.vy;
                    if (n.x < -20) n.x = width + 20;
                    if (n.x > width + 20) n.x = -20;
                    if (n.y < -20) n.y = height + 20;
                    if (n.y > height + 20) n.y = -20;
                }
            }

            for (let i = 0; i < nodes.length; i += 1) {
                for (let j = i + 1; j < nodes.length; j += 1) {
                    const a = nodes[i];
                    const b = nodes[j];
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const dist = Math.hypot(dx, dy);
                    if (dist > linkDist) continue;
                    const alpha = (1 - dist / linkDist) * 0.22;
                    ctx.strokeStyle = `hsla(${(a.hue + b.hue) / 2}, 85%, 72%, ${alpha})`;
                    ctx.lineWidth = 0.7;
                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);
                    ctx.stroke();
                }
            }

            for (const n of nodes) {
                const dx = pointer.x - n.x;
                const dy = pointer.y - n.y;
                const near = pointer.active ? Math.max(0, 1 - Math.hypot(dx, dy) / 220) : 0;
                const radius = n.r + near * 1.8;
                const glow = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, radius * 6);
                glow.addColorStop(0, `hsla(${n.hue}, 95%, 78%, ${0.85 + near * 0.15})`);
                glow.addColorStop(1, `hsla(${n.hue}, 95%, 70%, 0)`);
                ctx.fillStyle = glow;
                ctx.beginPath();
                ctx.arc(n.x, n.y, radius * 6, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = `hsla(${n.hue}, 100%, 88%, ${0.7 + near * 0.3})`;
                ctx.beginPath();
                ctx.arc(n.x, n.y, radius, 0, Math.PI * 2);
                ctx.fill();
            }
        };

        const loop = () => {
            draw();
            raf = window.requestAnimationFrame(loop);
        };

        const onPointerMove = (event: PointerEvent) => {
            const rect = canvas.getBoundingClientRect();
            pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top, active: true };
        };
        const onPointerLeave = () => {
            pointer = { ...pointer, active: false };
        };
        const onVisibility = () => {
            if (reduceMotion) return;
            if (document.hidden) {
                window.cancelAnimationFrame(raf);
            } else {
                raf = window.requestAnimationFrame(loop);
            }
        };

        resize();
        draw();
        if (!reduceMotion) raf = window.requestAnimationFrame(loop);

        const observer = new ResizeObserver(resize);
        observer.observe(canvas);
        window.addEventListener('pointermove', onPointerMove, { passive: true });
        window.addEventListener('pointerleave', onPointerLeave);
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            window.cancelAnimationFrame(raf);
            observer.disconnect();
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerleave', onPointerLeave);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ opacity: 0.62, zIndex: 1 }}
        />
    );
};

export default HeroAmbience;
